import { DefaultValues, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useAlert } from 'shared/components/alerts/use-alert';
import { useUploadIdSubmission } from './use-upload-id-submission';
import { idSubmissionFormSchema, IdSubmissionFormValues } from '../schema';
import { DocumentImagesResult } from '../steps/id-scan-manual-step/types';
import { getBase64PrefixForJpeg } from '../utils/get-base64-prefix-for-jpeg';

const DEFAULT_VALUES: DefaultValues<IdSubmissionFormValues> = {
  selfieImage: '',
  documentImages: {
    front: '',
    rear: '',
  },
  email: '',
  termsAndConditionsAccepted: false,
};

export const useIdSubmissionForm = ({ onNextStep }: { onNextStep: () => void }) => {
  const form = useForm<IdSubmissionFormValues>({
    resolver: zodResolver(idSubmissionFormSchema),
    defaultValues: DEFAULT_VALUES,
    mode: 'onChange',
  });

  const { mutate: uploadIdSubmission, isLoading: isSubmitting } = useUploadIdSubmission();
  const { alertError } = useAlert();

  const onSubmit = (values: IdSubmissionFormValues) => {
    uploadIdSubmission(
      {
        documentImages: {
          front: values.documentImages.front,
          rear: values.documentImages.rear,
        },
        selfieImage: getBase64PrefixForJpeg(values.selfieImage),
        email: values.email,
      },
      {
        onSuccess: () => {
          onNextStep();
        },
        onError: (error) => {
          alertError(error);
        },
      }
    );
  };

  const handleSelfieSuccess = (selfieImage: string) => {
    form.setValue('selfieImage', selfieImage, { shouldValidate: true });
    onNextStep();
  };

  const handleDocumentSuccess = ({ front, rear }: DocumentImagesResult) => {
    form.setValue('documentImages.front', front, { shouldValidate: true });
    form.setValue('documentImages.rear', rear, { shouldValidate: true });
  };

  return {
    form,
    onSubmit,
    handleSelfieSuccess,
    handleDocumentSuccess,
    isSubmitting,
  };
};
